import React from 'react';
import PropTypes from 'prop-types';
import ModalHost from './ModalHost';
import Janitor from '../../modules/Janitor';

/**
 * Adapts the third generation of modals to the plumbing for the second generation of modals (which
 * use modalManager). Provides the plumbing sufficient to support use with `modalManager` while
 * supporting styles and animations associated with the third generation of modals (using
 * ModalTrigger and friends.)
 *
 * Used by the `ManagedModal` class to provide functionality similar to what was provided by
 * `modalManager`
 */
export default class ModalAdapter extends React.Component {
  static propTypes = {
    /**  Always `ModalTrigger`. */
    triggerType: PropTypes.elementType.isRequired,
    /** Renders a modal, passed a function to close the modal */
    renderModal: PropTypes.func.isRequired,
    /** Renders the trigger for the modal, passed a function to open the modal */
    renderTrigger: PropTypes.func,
    /** Called when the trigger is clicked */
    onTriggerClick: PropTypes.func,
    /** Called when the modal is opened */
    onModalOpen: PropTypes.func,
    /** Called when the modal is closed */
    onModalClose: PropTypes.func,
    /** True to initially open the modal, otherwise false */
    initialOpen: PropTypes.bool,
    /** Required by modalManager. Not used */
    renderContainer: PropTypes.any.isRequired,
    /** Props passed to the ModalHost wrapper */
    hostProps: PropTypes.object,
  };

  static defaultProps = {
    renderTrigger: null,
    onTriggerClick: null,
    onModalOpen: null,
    onModalClose: null,
    initialOpen: false,
    hostProps: {},
  };

  state = {
    closed: false,
  };

  janitor = new Janitor();

  closeCallback = null;

  openCallback = null;

  componentDidMount() {
    // open the modal
    if (typeof this.openCallback === 'function') {
      this.openCallback();
    }
  }

  componentWillUnmount() {
    this.janitor.cleanup();
  }

  /**
   * Closes the modal, setting a state variable to ensure the modal cannot be opened again. This
   * function is used by `AngularModalTrigger` to provide time for exit animation.
   * @returns {Promise} A promise which resolves when the modal has animated away.
   */
  closeModal() {
    this.setState({ closed: true });
    // closes the modal, resolving a promise when the modal has completely closed
    return this.janitor.addPromise(new Promise(resolve => {
      if (typeof this.closeCallback === 'function') {
        this.closeCallback();
      }

      setTimeout(() => resolve(), 300);
    }));
  }

  /**
   * Wraps the `renderModal` function provided to `ModalAdapter`, storing the callback used to close
   * the modal. This function also checks the state to ensure that the modal can only be opened
   * once.
   */
  wrapRenderModal = close => {
    const { renderModal } = this.props;
    this.closeCallback = close;
    if (!this.state.closed) {
      return renderModal(close);
    }

    return undefined;
  };

  /**
   * Wraps the `renderTrigger` function provided to `ModalAdapter`, storing the callback used to
   * open the modal.
   */
  wrapRenderTrigger = open => {
    const { renderTrigger } = this.props;
    this.openCallback = open;
    return renderTrigger(open);
  };

  render() {
    const {
      triggerType: ModalTrigger,
      // renderContainer isn't used, but is provided by modalManager and needs to be filtered out
      renderContainer,
      renderModal,
      renderTrigger,
      hostProps,
      ...props
    } = this.props;

    return (
      <ModalHost {...hostProps} isRegistered>
        <ModalTrigger
          renderModal={this.wrapRenderModal}
          renderTrigger={this.wrapRenderTrigger}
          {...props}
        />
      </ModalHost>
    );
  }
}
